import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Boxes, Factory, PackageCheck, Send } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { MediaImage } from '../components/MediaImage'
import { useAuth } from '../contexts/auth'
import { apiError, fmtDate, fmtDateTime, type MaterialRow } from '../lib/industria'
import {
  ORDER_STATUS_BADGE, ORDER_STATUS_LABEL, REQUEST_STATUS_BADGE, REQUEST_STATUS_LABEL,
  type MyProduction,
} from '../lib/producao'
import type { Product } from '@wsfull/shared'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

// Portal da marca. Tudo aqui já vem com escopo do dono pela API — o cliente não
// vê custo, fornecedor nem material de outra marca.
export function MinhaProducaoPage() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ productId: '', qty: '', desiredDate: '', notes: '' })

  const { data, isLoading } = useQuery<MyProduction>({
    queryKey: ['minha-producao'],
    queryFn: async () => (await api.get('/my-production')).data,
  })

  const { data: materials = [] } = useQuery<MaterialRow[]>({
    queryKey: ['minha-producao', 'materials'],
    queryFn: async () => (await api.get('/materials')).data,
  })

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', user?.tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId: user?.tenantId } })).data,
    enabled: open && !!user?.tenantId,
  })

  const requestMutation = useMutation({
    mutationFn: async () => (await api.post('/production-requests', {
      productId: form.productId,
      qty: Number(form.qty),
      desiredDate: form.desiredDate || null,
      notes: form.notes.trim() || null,
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['minha-producao'] })
      setOpen(false)
      setForm({ productId: '', qty: '', desiredDate: '', notes: '' })
    },
    onError: (e) => setError(apiError(e, 'Não foi possível enviar a solicitação')),
  })

  const orders = data?.orders ?? []
  const requests = data?.requests ?? []
  const produced = data?.produced ?? []
  const running = orders.filter(o => !['closed', 'cancelled'].includes(o.status))

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Minha produção</h2>
          <p className="text-sm text-gray-500">
            O material que é seu, o que virou produto e o que você pediu para produzir
          </p>
        </div>
        <Button onClick={() => { setError(null); setOpen(true) }}>
          <Send className="h-4 w-4" /> Solicitar produção
        </Button>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-50 p-2.5"><Factory className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{running.length}</p>
              <p className="text-xs text-gray-500">ordens em andamento</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3">
            <div className="rounded-xl bg-green-50 p-2.5"><PackageCheck className="h-5 w-5 text-green-600" /></div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {produced.reduce((a, p) => a + p.qty, 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-xs text-gray-500">unidades produzidas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3">
            <div className="rounded-xl bg-amber-50 p-2.5"><Boxes className="h-5 w-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{materials.length}</p>
              <p className="text-xs text-gray-500">materiais seus na WS</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Meu material na WS</span>
          <span className="text-xs text-gray-500">saldo do que é seu, separado de qualquer outra marca</span>
        </CardHeader>
        <CardContent className="p-0">
          {materials.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">
              Nenhum material seu em poder da WS
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-14 px-4 py-2.5 text-left font-medium">Foto</th>
                    <th className="px-4 py-2.5 text-left font-medium">Material</th>
                    <th className="px-4 py-2.5 text-right font-medium">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {materials.map(m => (
                    <tr key={m.id}>
                      <td className="px-4 py-2.5">
                        <MediaImage mediaId={m.photoMediaId} className="h-9 w-9 rounded-lg" />
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{m.name}</span>
                        <span className="block font-mono text-xs text-gray-400">{m.code}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {m.quantity.toLocaleString('pt-BR')} <span className="text-xs text-gray-400">{m.unit}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Minhas ordens</span></CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Carregando...</p>
          ) : orders.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">
              Nenhuma ordem — peça uma produção pelo botão acima
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Ordem</th>
                    <th className="px-4 py-2.5 text-left font-medium">Produto</th>
                    <th className="px-4 py-2.5 text-right font-medium">Planejado</th>
                    <th className="px-4 py-2.5 text-right font-medium">Produzido</th>
                    <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td className="px-4 py-2.5 font-mono text-gray-900">{o.number}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{o.productName}</span>
                        <span className="block font-mono text-xs text-gray-400">{o.productSku}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">
                        {o.qtyPlanned.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {o.qtyProduced ? o.qtyProduced.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{o.lotNumber ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={ORDER_STATUS_BADGE[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Minhas solicitações</span></CardHeader>
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhuma solicitação enviada</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {requests.map(r => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                  <div>
                    <span className="text-gray-900">{r.productName}</span>
                    <span className="block text-xs text-gray-400">
                      {r.qty.toLocaleString('pt-BR')} un
                      {r.desiredDate ? ` · para ${fmtDate(r.desiredDate)}` : ''}
                      {` · enviada ${fmtDateTime(r.createdAt)}`}
                    </span>
                    {r.decisionNotes && (
                      <span className="block text-xs text-gray-500">resposta: {r.decisionNotes}</span>
                    )}
                  </div>
                  <div className="text-right">
                    <Badge variant={REQUEST_STATUS_BADGE[r.status]}>{REQUEST_STATUS_LABEL[r.status]}</Badge>
                    {r.orderNumber && (
                      <span className="block font-mono text-xs text-gray-500">{r.orderNumber}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {produced.length > 0 && (
        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Já produzido</span></CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-gray-50">
              {produced.map(p => (
                <li key={p.productId} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <div>
                    <span className="text-gray-900">{p.productName}</span>
                    <span className="block font-mono text-xs text-gray-400">
                      {p.productSku}{p.lastAt ? ` · último em ${fmtDateTime(p.lastAt)}` : ''}
                    </span>
                  </div>
                  <span className="font-mono text-gray-900">{p.qty.toLocaleString('pt-BR')} un</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Modal title="Solicitar produção" open={open} onClose={() => setOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); requestMutation.mutate() }}
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Produto</label>
            <select
              className={selectClass}
              value={form.productId}
              onChange={e => setForm({ ...form, productId: e.target.value })}
              required
            >
              <option value="">Selecione...</option>
              {products.filter(p => p.active).map(p => (
                <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Quantidade"
              type="number"
              min={1}
              value={form.qty}
              onChange={e => setForm({ ...form, qty: e.target.value })}
              required
            />
            <Input
              label="Data desejada"
              type="date"
              value={form.desiredDate}
              onChange={e => setForm({ ...form, desiredDate: e.target.value })}
            />
          </div>

          <Input
            label="Observações"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="lote para a campanha de setembro"
          />

          <p className="text-xs text-gray-500">
            A WS responde aceitando ou recusando. Aceita, vira ordem de produção e você acompanha
            por aqui.
          </p>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={requestMutation.isPending} disabled={!form.productId}>
              Enviar solicitação
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
